import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react'

import { useAbortController } from 'core/ui/hooks/useAbortController'

import { type Shipment } from '../../domain/Shipment'
import { useDriverLocation } from '../hooks/useDriverLocation'

type ShipmentLocationsContextValue = Omit<ReturnType<typeof useDriverLocation>, 'getLocations'> & {
  readonly refetchLocations: () => Promise<void>
}

type ShipmentLocationsProviderProps = {
  readonly shipment: Shipment
  readonly children: ReactNode
}

const ShipmentLocationsContext = createContext<ShipmentLocationsContextValue | undefined>(undefined)

export function ShipmentLocationsProvider({ shipment, children }: ShipmentLocationsProviderProps) {
  const { getLocations, ...rest } = useDriverLocation()
  const abortController = useAbortController()

  useEffect(() => {
    getLocations(shipment.uuid, abortController)

    return () => {
      abortController.abort()
    }
  }, [shipment.uuid, getLocations, abortController])

  const refetchLocations = useCallback(
    () => getLocations(shipment.uuid, abortController),
    [abortController, getLocations, shipment.uuid]
  )

  const value = useMemo(() => ({ refetchLocations, ...rest }), [refetchLocations, rest])

  return (
    <ShipmentLocationsContext.Provider value={value}>{children}</ShipmentLocationsContext.Provider>
  )
}

export function useShipmentLocationsContext(): ShipmentLocationsContextValue {
  const context = useContext(ShipmentLocationsContext)

  if (!context) {
    throw new Error('useShipmentLocations must be used within a ShipmentLocationsProvider')
  }

  return context
}
